// Period grid: a values table with one column per period (cutoff dates across the top, Julio's Oct 8 request) and,
// optionally, a chart drawn on the same columns above or below it (the PPCI / PRO report layout Julio sent).
// Table and chart share one horizontal scroller, so they always scroll together; the row labels and the chart's
// axes stay pinned at the sides. Printing splits the columns into page-width chunks, each a full table + chart.
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { scaleLinear } from "@visx/scale";
import { LinePath, Line } from "@visx/shape";
import { AxisLeft, AxisRight } from "@visx/axis";
import { curveLinear } from "@visx/curve";
import { cn } from "@/components/ui";
import type { SeriesDef } from "@/components/charts";
import { useNeutral, usePrinting } from "@/lib/theme";

export interface GridColumn {
  period: string;
  year: string;
  dayMonth: string;
  /** The column holding the as-of cutoff. */
  current?: boolean;
}

export interface GridRow {
  /** Section label, shown on the first row of each run of rows with the same group. */
  group?: string;
  label: string;
  values: (number | null)[];
  fmt: (v: number) => string;
  color?: string;
  strong?: boolean;
}

export interface GridChart {
  height?: number;
  /** Left axis. A line's value for a column is plotted at that column's cutoff (its right edge). */
  lines: SeriesDef[];
  /** Start every line at 0 on the left edge of the first column (cumulative curves). */
  origin?: boolean;
  /** Right axis: one group of bars per column. */
  bars?: SeriesDef[];
  leftFormat: (v: number) => string;
  rightFormat?: (v: number) => string;
  leftTitle?: string;
  rightTitle?: string;
  /** Horizontal reference lines on the left axis (e.g. productivity 1.0). */
  refLines?: number[];
  /** Values the left axis must include. */
  leftInclude?: number[];
}

/** Viewbox width of one column. The plot stretches to the real column width (preserveAspectRatio none). */
const U = 100;
const MT = 10;
const MB = 6;
const PRINT_COLS = 20;

export function PeriodGrid({
  columns,
  rows,
  chart,
  marker,
  tableFirst = true,
  colWidth = 64,
  landscape,
}: {
  columns: GridColumn[];
  rows: GridRow[];
  chart?: GridChart;
  /** Column index of the as-of cutoff: a dashed line on the chart at its right edge. */
  marker?: number;
  /** Table above the chart (true) or below it. */
  tableFirst?: boolean;
  colWidth?: number;
  /** Print this page in landscape. */
  landscape?: boolean;
}) {
  const printing = usePrinting();
  const n = columns.length;
  if (!n) return null;
  const scales = chart ? chartScales(chart, chart.height ?? 300) : null;
  // Screen: one block with every column, scrolled sideways. Print: evenly sized chunks of at most PRINT_COLS columns.
  const chunks: [number, number][] = [];
  if (printing) {
    const count = Math.ceil(n / PRINT_COLS);
    const per = Math.ceil(n / count);
    for (let a = 0; a < n; a += per) chunks.push([a, Math.min(n, a + per)]);
  } else chunks.push([0, n]);
  return (
    <div>
      {landscape && <style>{"@media print { @page { size: landscape; } }"}</style>}
      {chunks.map(([a, b], k) => (
        <Block key={a} {...{ columns, rows, chart, scales, marker, tableFirst, colWidth }} from={a} to={b} fluid={printing} breakBefore={k > 0} />
      ))}
      {chart && <GridLegend chart={chart} />}
    </div>
  );
}

function chartScales(chart: GridChart, height: number) {
  const vals = (s: SeriesDef[]) => s.flatMap((x) => x.values.filter((v): v is number => v !== null && isFinite(v)));
  const left = [...vals(chart.lines), ...(chart.origin ? [0] : []), ...(chart.refLines ?? []), ...(chart.leftInclude ?? [])];
  const lo = Math.min(0, ...left);
  const hi = left.length ? Math.max(1e-9, ...left) : 1;
  const y = scaleLinear({ domain: [lo, hi + (hi - lo) * 0.04], range: [height - MB, MT], nice: true });
  const right = chart.bars ? vals(chart.bars) : [];
  const yr = scaleLinear({ domain: [0, right.length ? Math.max(1e-9, ...right) * 1.05 : 1], range: [height - MB, MT], nice: true });
  return { y, yr, height };
}

type Scales = ReturnType<typeof chartScales>;

function Block({
  columns,
  rows,
  chart,
  scales,
  marker,
  tableFirst,
  colWidth,
  from,
  to,
  fluid,
  breakBefore,
}: {
  columns: GridColumn[];
  rows: GridRow[];
  chart?: GridChart;
  scales: Scales | null;
  marker?: number;
  tableFirst: boolean;
  colWidth: number;
  from: number;
  to: number;
  fluid: boolean;
  breakBefore: boolean;
}) {
  const N = useNeutral();
  const scroller = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<number | null>(null);
  const n = to - from;
  const cols = columns.slice(from, to);
  const rowH = fluid ? 16 : 22;
  const labelW = fluid ? 160 : 216;
  const rightW = chart?.bars ? (fluid ? 44 : 56) : 0;
  const cell = fluid ? "text-[8px]" : "text-xs";

  // Open with the as-of cutoff in view, a little right of centre.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (fluid || !el || marker === undefined || marker < 0) return;
    el.scrollLeft = Math.max(0, (marker + 1) * colWidth - el.clientWidth * 0.6);
  }, [fluid, marker, colWidth, n]);

  const grid = { display: "grid", gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, height: rowH };
  const groupStart = (i: number) => i === 0 || rows[i].group !== rows[i - 1].group;
  const head: [string, (c: GridColumn) => string][] = [
    ["Period", (c) => c.period],
    ["Year", (c) => c.year],
    ["Day month", (c) => c.dayMonth],
  ];

  const labels = (
    <div className={cell}>
      {head.map(([h], k) => (
        <div key={h} style={{ height: rowH }} className={cn("flex items-center justify-end bg-slate-50 px-2 font-medium text-slate-500", k === 2 && "border-b border-slate-200")}>
          {h}
        </div>
      ))}
      {rows.map((r, i) => (
        <div key={i} style={{ height: rowH }} className={cn("flex items-center", groupStart(i) && i > 0 && "border-t border-slate-200")}>
          <span className="w-[52%] truncate px-2 text-slate-500">{groupStart(i) ? r.group : ""}</span>
          <span className={cn("flex-1 truncate pr-2 text-right", r.strong && "font-semibold")} style={{ color: r.color }}>
            {r.label}
          </span>
        </div>
      ))}
    </div>
  );
  const blankRows = <div style={{ height: rowH * (head.length + rows.length) }} />;

  const shade = (j: number) => (hover === j ? "bg-slate-100" : cols[j].current ? "bg-brand-50" : "");
  const table = (
    <div className={cn("num", cell)}>
      {head.map(([h, f], k) => (
        <div key={h} style={grid} className={cn("bg-slate-50 font-medium text-slate-500", k === 2 && "border-b border-slate-200")}>
          {cols.map((c, j) => (
            <div key={j} className={cn("flex items-center justify-center border-l border-slate-100 px-0.5 whitespace-nowrap", c.current && "font-semibold text-slate-800", shade(j))}>
              {f(c)}
            </div>
          ))}
        </div>
      ))}
      {rows.map((r, i) => (
        <div key={i} style={grid} className={cn(groupStart(i) && i > 0 && "border-t border-slate-200", r.strong && "font-semibold")}>
          {r.values.slice(from, to).map((v, j) => (
            <div key={j} className={cn("flex items-center justify-end overflow-hidden border-l border-slate-100 px-1 whitespace-nowrap", shade(j))}>
              {v === null || !isFinite(v) ? "" : r.fmt(v)}
            </div>
          ))}
        </div>
      ))}
    </div>
  );

  const plot = chart && scales && (
    <svg width="100%" height={scales.height} viewBox={`0 0 ${n * U} ${scales.height}`} preserveAspectRatio="none" className="block" role="img" aria-label="chart on the period columns">
      {hover !== null && <rect x={hover * U} y={0} width={U} height={scales.height} fill={N.hover} />}
      {cols.map((c, j) => (c.current ? <rect key={j} x={j * U} y={0} width={U} height={scales.height} fill={N.sunken} /> : null))}
      {scales.y.ticks(5).map((t) => (
        <Line key={t} from={{ x: 0, y: scales.y(t) }} to={{ x: n * U, y: scales.y(t) }} stroke={N.grid} vectorEffect="non-scaling-stroke" />
      ))}
      {cols.map((_, j) => (
        <Line key={j} from={{ x: j * U, y: 0 }} to={{ x: j * U, y: scales.height }} stroke={N.grid} vectorEffect="non-scaling-stroke" />
      ))}
      {chart.bars?.map((s, k, all) => {
        const w = (U * 0.72) / all.length;
        return s.values.slice(from, to).map((v, j) =>
          v === null || !isFinite(v) || v <= 0 ? null : (
            <rect
              key={`${s.key}${j}`}
              x={j * U + U * 0.14 + k * w + w * 0.06}
              y={scales.yr(v)}
              width={w * 0.88}
              height={scales.yr(0) - scales.yr(v)}
              fill={s.color}
              fillOpacity={0.28}
              stroke={s.color}
              strokeOpacity={0.8}
              strokeDasharray={s.dashed ? "3 2" : undefined}
              vectorEffect="non-scaling-stroke"
            />
          ),
        );
      })}
      {chart.refLines?.map((v) => (
        <Line key={v} from={{ x: 0, y: scales.y(v) }} to={{ x: n * U, y: scales.y(v) }} stroke={N.label} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
      ))}
      {marker !== undefined && marker >= from && marker < to && (
        <Line from={{ x: (marker - from + 1) * U, y: 0 }} to={{ x: (marker - from + 1) * U, y: scales.height }} stroke={N.label} strokeDasharray="5 4" vectorEffect="non-scaling-stroke" />
      )}
      {chart.lines.map((s) => {
        const pts: [number, number][] = [];
        // The cumulative curve starts at 0 on the first column's left edge; a later chunk picks up from the previous cutoff.
        if (chart.origin && from === 0) pts.push([0, 0]);
        else if (from > 0 && isNum(s.values[from - 1])) pts.push([0, s.values[from - 1]!]);
        s.values.slice(from, to).forEach((v, j) => isNum(v) && pts.push([(j + 1) * U, v]));
        return (
          <LinePath<[number, number]>
            key={s.key}
            data={pts}
            x={(d) => d[0]}
            y={(d) => scales.y(d[1])}
            stroke={s.color}
            strokeWidth={2}
            strokeDasharray={s.dashed ? "6 4" : undefined}
            curve={curveLinear}
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </svg>
  );

  const leftAxis = chart && scales && (
    <svg width={labelW} height={scales.height} className="block">
      {chart.leftTitle && (
        <text transform={`translate(12,${scales.height / 2}) rotate(-90)`} textAnchor="middle" fontSize={fluid ? 9 : 11} fill={N.tick}>
          {chart.leftTitle}
        </text>
      )}
      <AxisLeft left={labelW - 1} scale={scales.y} numTicks={5} tickFormat={(v) => chart.leftFormat(Number(v))} stroke={N.axis} tickStroke={N.axis} tickLabelProps={{ fontSize: fluid ? 8 : 10, fill: N.tick }} />
    </svg>
  );
  const rightAxis = chart?.bars && scales && (
    <svg width={rightW} height={scales.height} className="block">
      <AxisRight scale={scales.yr} numTicks={5} tickFormat={(v) => (chart.rightFormat ?? String)(Number(v))} stroke={N.axis} tickStroke={N.axis} tickLabelProps={{ fontSize: fluid ? 8 : 10, fill: N.tick }} />
    </svg>
  );

  const stack = (t: ReactNode, c: ReactNode) => (tableFirst ? [t, c] : [c, t]);
  const [l1, l2] = stack(labels, leftAxis);
  const [m1, m2] = stack(table, plot);
  const [r1, r2] = stack(blankRows, rightAxis);
  const pick = (e: React.MouseEvent<HTMLDivElement>) => {
    const box = e.currentTarget.getBoundingClientRect();
    setHover(Math.max(0, Math.min(n - 1, Math.floor(((e.clientX - box.left) / box.width) * n))));
  };
  return (
    <div className={cn("flex overflow-hidden rounded-md border border-slate-200", breakBefore && "mt-4 print:break-before-page")}>
      <div style={{ width: labelW }} className="shrink-0 border-r border-slate-200">
        {l1}
        {l2}
      </div>
      <div ref={scroller} className={cn("min-w-0 flex-1", !fluid && "overflow-x-auto")}>
        <div style={fluid ? undefined : { width: n * colWidth }} onMouseMove={pick} onMouseLeave={() => setHover(null)}>
          {m1}
          {m2}
        </div>
      </div>
      {rightW > 0 && (
        <div style={{ width: rightW }} className="shrink-0 border-l border-slate-200">
          {r1}
          {r2}
        </div>
      )}
    </div>
  );
}

const isNum = (v: number | null | undefined): v is number => v !== null && v !== undefined && isFinite(v);

// Swatches are SVG so they print without background graphics.
function GridLegend({ chart }: { chart: GridChart }) {
  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs">
      {chart.lines.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1">
          <svg width={16} height={4} aria-hidden>
            <line x1={0} y1={2} x2={16} y2={2} stroke={s.color} strokeWidth={3} strokeDasharray={s.dashed ? "4 2" : undefined} />
          </svg>
          {s.label}
        </span>
      ))}
      {chart.bars?.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1">
          <svg width={10} height={10} aria-hidden>
            <rect x={0.5} y={0.5} width={9} height={9} fill={s.color} fillOpacity={0.28} stroke={s.color} />
          </svg>
          {s.label}
        </span>
      ))}
      {chart.bars?.length ? <span className="text-slate-500">· bars on the right axis{chart.rightTitle ? ` (${chart.rightTitle})` : ""}</span> : null}
    </div>
  );
}
